/**
 * Choosing the map.
 *
 * Kate, after playing: "you still haven't made it so you can change maps." It existed, but
 * only as a keyboard key gated to offline play — and she plays on an iPad, so there was no
 * route to it at all.
 *
 * Her original rule still holds online: "if everyone agrees on it, you can change the map."
 * So offline this switches immediately, and online it opens a vote the room settles.
 */
import { BIOMES, BIOME_ORDER, type BiomeId } from '../world/biomes'

export class MapScreen {
  private readonly root: HTMLDivElement
  /** Offline: change now. Online: start a vote. */
  onChoose: ((biome: BiomeId, online: boolean) => void) | null = null
  isOnline: () => boolean = () => false
  currentBiome: () => BiomeId = () => 'jungle'
  onOpenChange: ((open: boolean) => void) | null = null

  constructor() {
    this.root = document.createElement('div')
    this.root.className = 'mapscreen'
    this.root.hidden = true
    document.body.append(this.root)
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  open(): void {
    this.render()
    this.root.hidden = false
    this.onOpenChange?.(true)
  }

  close(): void {
    this.root.hidden = true
    this.onOpenChange?.(false)
  }

  private render(): void {
    const online = this.isOnline()
    const current = this.currentBiome()
    this.root.innerHTML = ''

    const panel = document.createElement('div')
    panel.className = 'mapscreen-panel'

    const title = document.createElement('h2')
    title.textContent = 'Change the map'
    panel.append(title)

    const note = document.createElement('p')
    note.className = 'mapscreen-note'
    note.textContent = online
      ? 'Everyone has to agree. Picking one asks the other players to vote.'
      : 'Pick a map. Playing on your own, it changes straight away.'
    panel.append(note)

    const grid = document.createElement('div')
    grid.className = 'mapscreen-grid'
    for (const id of BIOME_ORDER) {
      const spec = BIOMES[id]
      const card = document.createElement('button')
      card.className = `mapscreen-card${id === current ? ' is-current' : ''}`
      card.style.setProperty('--ground', `#${spec.ground.toString(16).padStart(6, '0')}`)
      card.style.setProperty('--sky', `#${spec.sky.toString(16).padStart(6, '0')}`)
      card.innerHTML = `
        <span class="mapscreen-swatch"></span>
        <span class="mapscreen-name">${spec.name}</span>
        <span class="mapscreen-desc">${describe(id)}</span>
        ${id === current ? '<span class="mapscreen-current">you are here</span>' : ''}`
      card.addEventListener('click', () => {
        this.onChoose?.(id, online)
        this.close()
      })
      grid.append(card)
    }
    panel.append(grid)

    const done = document.createElement('button')
    done.className = 'mapscreen-done'
    done.textContent = 'Back'
    done.addEventListener('click', () => this.close())
    panel.append(done)

    this.root.append(panel)
  }
}

/** What each map is, in the terms Kate used for it. */
function describe(id: BiomeId): string {
  switch (id) {
    case 'jungle': return 'Thick trees. The one you start on.'
    case 'desert': return 'Sand and camels.'
    case 'ocean': return 'All water. Dolphins, whales and fish.'
    case 'plains': return 'Oak trees, not forest. Wide open.'
    case 'mountain': return 'Cold and bare. Rock, thin grass, hardly any trees.'
  }
}
